// ═══════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════
import { useState, useRef, useEffect, useCallback } from "react";
import { V } from "../theme.js";

// ── Color conversion utilities ──
export function hx(h) { const s = h || "#000000"; return { r: parseInt(s.slice(1, 3), 16) || 0, g: parseInt(s.slice(3, 5), 16) || 0, b: parseInt(s.slice(5, 7), 16) || 0 }; }
export function hx2(h) { const s = h || "#000000"; return { r2: parseInt(s.slice(1, 3), 16) || 0, g2: parseInt(s.slice(3, 5), 16) || 0, b2: parseInt(s.slice(5, 7), 16) || 0 }; }
export function rgbHex(r, g, b) { return "#" + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join(""); }
export function rgbToHsl(r, g, b) { r /= 255; g /= 255; b /= 255; const max = Math.max(r, g, b), min = Math.min(r, g, b); let h = 0, s = 0, l = (max + min) / 2; if (max !== min) { const d = max - min; s = l > 0.5 ? d / (2 - max - min) : d / (max + min); switch (max) { case r: h = (g - b) / d + (g < b ? 6 : 0); break; case g: h = (b - r) / d + 2; break; case b: h = (r - g) / d + 4; break; } h /= 6; } return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)]; }

// ── Separator ──
export function OptSep() { return (<div style={{ width: 1, height: 16, background: V.panelBorder, margin: "0 4px" }}/>); }

// ── Option bar button ──
export function OptBtn({ children, onClick, style = {}, accent }) {
  return (
    <div onClick={onClick}
      style={{ padding: "3px 8px", fontSize: 10, fontFamily: V.mono, background: accent ? V.accentDim : V.panelActive, border: `1px solid ${accent ? V.accent : V.panelBorder2}`, color: accent ? V.accentL : V.muted, borderRadius: 3, cursor: "pointer", transition: "all 0.12s", whiteSpace: "nowrap", ...style }}
      onMouseEnter={e => { e.currentTarget.style.color = accent ? V.accentL : V.text; e.currentTarget.style.borderColor = accent ? V.accentL : "#555"; }}
      onMouseLeave={e => { e.currentTarget.style.color = accent ? V.accentL : V.muted; e.currentTarget.style.borderColor = accent ? V.accent : V.panelBorder2; }}>{children}</div>
  );
}

// ── Layer action button ──
export function LayerAction({ children, onClick, t, danger, label }) {
  return (
    <div onClick={onClick} title={t}
      style={{ height: 28, padding: "0 8px", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, borderRadius: 4, cursor: "pointer", color: V.muted, background: V.panelActive, border: `1px solid ${V.panelBorder2}`, fontSize: 12, transition: "all 0.12s", whiteSpace: "nowrap" }}
      onMouseEnter={e => { e.currentTarget.style.background = V.panelHover; e.currentTarget.style.color = danger ? "#f87171" : V.text; e.currentTarget.style.borderColor = danger ? "#f87171" : "#555"; }}
      onMouseLeave={e => { e.currentTarget.style.background = V.panelActive; e.currentTarget.style.color = V.muted; e.currentTarget.style.borderColor = V.panelBorder2; }}>
      <span style={{ fontSize: 12, lineHeight: 1 }}>{children}</span>
      {label && <span style={{ fontSize: 9, letterSpacing: "0.3px" }}>{label}</span>}
    </div>
  );
}

// ── Collapsible panel section ──
export function PanelSection({ title, children, collapsed, toggle, flex }) {
  return (
    <div style={{ borderBottom: `1px solid ${V.panelBorder}`, flexShrink: 0, ...(flex ? { flex: 1, display: "flex", flexDirection: "column" } : {}) }}>
      <div onClick={toggle} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", cursor: "pointer", transition: "background 0.1s" }}
        onMouseEnter={e => e.currentTarget.style.background = V.panelHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: V.muted }}>{title}</span>
        <span style={{ color: V.ghost, fontSize: 10, transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>▾</span>
      </div>
      {!collapsed && <div style={{ padding: 8 }}>{children}</div>}
    </div>
  );
}

// ── Info cell (for Info panel) ──
export function InfoCell({ label, val }) {
  return (
    <div style={{ background: V.panelActive, borderRadius: 3, padding: "5px 7px" }}>
      <div style={{ fontSize: 9, color: V.ghost, marginBottom: 2, letterSpacing: "0.5px" }}>{label}</div>
      <div style={{ fontSize: 11, color: V.accentL, fontWeight: 500 }}>{val}</div>
    </div>
  );
}

// ── Adjustment row (label + slider) ──
export function AdjRow({ label, value, min, max, onChange, fmt }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
        <span style={{ fontSize: 10, color: V.muted }}>{label}</span>
        <span style={{ fontSize: 10, color: V.text, fontWeight: 500 }}>{fmt ? fmt(value) : value}</span>
      </div>
      <input type="range" min={min} max={max} value={value} onChange={e => onChange(+e.target.value)}/>
    </div>
  );
}

// ── Draggable Modal ──
export function Modal({ children, onClose }) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const onDragStart = useCallback(e => {
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
    setDragging(true); dragStart.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
  }, [pos]);
  useEffect(() => {
    if (!dragging) return;
    const onM = e => { setPos({ x: dragStart.current.px + e.clientX - dragStart.current.x, y: dragStart.current.py + e.clientY - dragStart.current.y }); };
    const onU = () => setDragging(false);
    window.addEventListener("mousemove", onM); window.addEventListener("mouseup", onU);
    return () => { window.removeEventListener("mousemove", onM); window.removeEventListener("mouseup", onU); };
  }, [dragging]);
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} onMouseDown={onDragStart}
        style={{ background: V.panelRaised, border: `1px solid ${V.panelBorder2}`, borderRadius: 8, padding: 20, minWidth: 320, maxWidth: 420, boxShadow: "0 12px 40px rgba(0,0,0,.6)", cursor: dragging ? "grabbing" : "grab", transform: `translate(${pos.x}px,${pos.y}px)`, userSelect: dragging ? "none" : "auto" }}>{children}</div>
    </div>
  );
}

// ── Layer thumbnail ──
export function LayerThumb({ layer, w, h }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c || !layer) return;
    const ctx = c.getContext("2d",{willReadFrequently:true});
    ctx.clearRect(0, 0, 30, 30);
    // Prefer the full canvas (single-thread mode), fall back to thumbnail ImageBitmap (worker mode)
    if (layer.c) {
      try { ctx.drawImage(layer.c, 0, 0, 30, 30); } catch (e) {}
    } else if (layer.thumbnail) {
      try { ctx.drawImage(layer.thumbnail, 0, 0, 30, 30); } catch (e) {}
    }
  });
  return (
    <div style={{ width: 30, height: 30, borderRadius: 2, border: `1px solid ${V.panelBorder2}`, flexShrink: 0, overflow: "hidden", background: "#0a0a0a" }}>
      <canvas ref={ref} width={30} height={30} style={{ width: "100%", height: "100%" }}/>
    </div>
  );
}

// ── Mask thumbnail ──
export function MaskThumb({ layer }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c || !layer?.mask) return;
    const ctx = c.getContext("2d",{willReadFrequently:true});
    ctx.clearRect(0, 0, 24, 24);
    try { ctx.drawImage(layer.mask, 0, 0, 24, 24); } catch (e) {}
  });
  return (
    <div style={{ width: 24, height: 24, borderRadius: 2, border: "1px solid #4ade8066", flexShrink: 0, overflow: "hidden", background: "#000" }}>
      <canvas ref={ref} width={24} height={24} style={{ width: "100%", height: "100%" }}/>
    </div>
  );
}

// ── Tool icon (SVG path parser for inline icons) ──
export function ToolIcon({ svg }) {
  if (!svg) return null;
  const parts = svg.split("|");
  const paths = []; const circles = []; const rects = []; const ellipses = [];
  parts.forEach(p => {
    const s = p.trim();
    if (s.startsWith("R")) { const v = s.slice(1).trim().split(/\s+/).map(Number); rects.push({ x: v[0], y: v[1], w: v[2], h: v[3], rx: v[4] || 0 }); }
    else if (s.startsWith("E")) { const v = s.slice(1).trim().split(/\s+/).map(Number); ellipses.push({ cx: v[0], cy: v[1], rx: v[2], ry: v[3] }); }
    else if (s.startsWith("M") || s.startsWith("m") || s.match(/^[0-9]/)) {
      const nums = s.trim().split(/\s+/).map(Number);
      if (nums.length === 3 && !isNaN(nums[0])) circles.push({ cx: nums[0], cy: nums[1], r: nums[2] });
      else paths.push(s);
    }
    else paths.push(s);
  });
  return (
    <svg viewBox="0 0 16 16" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.4}>
      {paths.map((d, i) => <path key={i} d={d}/>)}
      {circles.map((c, i) => <circle key={"c" + i} cx={c.cx} cy={c.cy} r={c.r}/>)}
      {rects.map((r, i) => <rect key={"r" + i} x={r.x} y={r.y} width={r.w} height={r.h} rx={r.rx}/>)}
      {ellipses.map((e, i) => <ellipse key={"e" + i} cx={e.cx} cy={e.cy} rx={e.rx} ry={e.ry}/>)}
    </svg>
  );
}
